import { createResource, type RouteResource } from 'react-resource-router';

import { QUERY_PARAMETER_QUERY_LABEL } from '@atlassian/eoc-alert-common';
import { fetch } from '@atlassian/eoc-fetch';
import {
	IDENTIFIER_RESPONDERS,
	LOGICAL_OPERATOR_AND,
	LOGICAL_OPERATOR_OR,
	tokenizer,
} from '@atlassian/eoc-query-editor';
import type { ResponderItemResponse } from '@atlassian/eoc-responder-names';

interface ResponderNamesResponse {
	values: ResponderItemResponse[];
}

import { removeQuotes } from '../../../common/utils';
import { getNoisyReductionMetrics } from '../endpoint';
import type { NoisyReductionMetrics } from '../types';

const MULTI_SELECT_VALUE_SEP = ',';

const extractResponderIds = (query: string | undefined): string[] => {
	if (!query) {
		return [];
	}

	const tokens = tokenizer(query);

	// Split tokens into groups by 'AND' and 'OR' operators
	const tokenGroups: (typeof tokens)[] = tokens.length
		? tokens.reduce<(typeof tokens)[]>(
				(acc, token) => {
					if (
						token.type === 'LOGICAL_OPERATOR' &&
						(token.value.toLowerCase() === LOGICAL_OPERATOR_AND.toLowerCase() ||
							token.value.toLowerCase() === LOGICAL_OPERATOR_OR.toLowerCase())
					) {
						acc.push([]);
					} else {
						acc[acc.length - 1].push(token);
					}
					return acc;
				},
				[[]],
			)
		: [];

	// Find ALL token groups that contain responders identifier
	const responderTokenGroups = tokenGroups.filter((tokenGroup) =>
		tokenGroup.find(
			(token) => token.type === 'IDENTIFIER' && token.value === IDENTIFIER_RESPONDERS,
		),
	);

	if (responderTokenGroups.length === 0) {
		return [];
	}

	// Extract responder IDs from all responder token groups
	const allResponderIds: string[] = [];
	for (const responderTokenGroup of responderTokenGroups) {
		// Find the value token (STRING_LITERAL or NUMBER_LITERAL) after the responders identifier
		const responderIndex = responderTokenGroup.findIndex(
			(token) => token.type === 'IDENTIFIER' && token.value === IDENTIFIER_RESPONDERS,
		);

		const valueToken = responderTokenGroup
			.slice(responderIndex + 1)
			.find((token) => token.type === 'STRING_LITERAL' || token.type === 'NUMBER_LITERAL');

		if (valueToken) {
			const responderIds = valueToken.value.split(MULTI_SELECT_VALUE_SEP).map(removeQuotes);
			allResponderIds.push(...responderIds);
		}
	}

	// Remove duplicates and return
	return Array.from(new Set(allResponderIds));
};

export const noiseReductionMetricsResource: RouteResource<NoisyReductionMetrics> =
	createResource<NoisyReductionMetrics>({
		type: 'noiseReductionMetricsResource',
		getKey: (routerContext) => {
			const query = routerContext.query[QUERY_PARAMETER_QUERY_LABEL];
			const responderIds = extractResponderIds(query);
			const responderIdsKey = responderIds.sort().join(',');
			return `noisyAlertsMetricsResource-${responderIdsKey}`;
		},
		getData: async (routerContext) => {
			const query = routerContext.query[QUERY_PARAMETER_QUERY_LABEL];
			const responderIds = extractResponderIds(query);

			// Filter out team responders by checking if they are teams
			let nonTeamResponderIds = responderIds;
			if (responderIds.length > 0) {
				// Check which responder IDs are teams
				const requestedResponders = responderIds.map((id) => ({
					id,
					type: 'team' as const,
				}));
				const responderNamesResponse = await fetch<ResponderNamesResponse>(
					`/v1/alerts/responders/names`,
					{
						method: 'POST',
						body: {
							responders: requestedResponders,
						},
					},
				);
				// Get the IDs that matched as teams
				const teamIds = new Set(
					responderNamesResponse.values
						.filter((responder: ResponderItemResponse) => responder.type === 'team')
						.map((responder: ResponderItemResponse) => responder.id),
				);
				// Filter out teams - keep only non-team responders
				nonTeamResponderIds = responderIds.filter((id) => !teamIds.has(id));
			}

			const response = await getNoisyReductionMetrics(nonTeamResponderIds);
			return response;
		},
		maxAge: 5 * 60 * 1000,
		isBrowserOnly: true,
	});
